/* eslint-disable max-len */
import { useCallback, useMemo } from 'react';
import { irsTerms, buildSpreadDiffStartDateLegs, parseBxtSxtInput } from 'lib/constants';
import parsePlot from 'lib/getFormattedLegs';
import { socketEmit } from 'services/socket';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { getAuth } from 'services/auth.service';
import { getTenor } from '@/helper/irs.helper.service';
import { getIrsSecurityByIrsIdTermUrl } from '@/services/irs.service';

const getIrsSecurity = async (authToken, { irsId, term }) => {
    const res = await getIrsSecurityByIrsIdTermUrl(authToken, { irsId, term });
    if (res?.data?.entity?.id) {
        return res?.data?.entity?.id;
    }
    return null;
};

const openInSamePage = (url) => {
    window.open(url, '_self');
};

const openTargetPage = (url) => {
    window.open(url, nanoid(5), popupSpecsForDes);
};

function parseGip(command) {
    const match = command.match(/\b\d+\b/);
    return match ? Number(match[0]) : null;
}

function handleTraceChronosBranch({
    searchPhrase,
    isIrsPage,
    irsId,
    chronos,
    openInSamePage,
    irsTerm
}) {
    if (searchPhrase.trim().toLowerCase().startsWith('trace') || searchPhrase.trim().toLowerCase().endsWith('trace')) {
        if (isIrsPage) {
            const toLower = searchPhrase.trim().toLowerCase();
            if (toLower === 'trace') {
                chronos({});
                return true;
            }
        }
        const HAS_ALL_REGEX = /\ball\b/i;
        const TENOR_LIST_REGEX = /\b\d+[ymwd](?:\/\d+[ymwd])*\b/i;
        let normalizedText = searchPhrase.toLowerCase()?.replace('spot', '').trim();
        if (HAS_ALL_REGEX.test(normalizedText)) {
            const hasCurveTenor = /\b\d+[ymdw]\d+[ymdw]\b/i.test(normalizedText);

            if (hasCurveTenor) {
                return false;
            }

            normalizedText = normalizedText
                .replace(/\bfwd\b.*$/i, '')
                .replace(/\ball\b/i, '')
                .replace(/\s+/g, ' ')
                .trim();

            const tenorMatch = normalizedText.match(TENOR_LIST_REGEX);

            if (tenorMatch) {
                const validTenors = tenorMatch[0]
                    .split('/')
                    .join('/');

                normalizedText = normalizedText.replace(TENOR_LIST_REGEX, validTenors);
            }

            const parts = normalizedText.split(' ');
            const hasExplicitTenor = Boolean(tenorMatch);

            if (!hasExplicitTenor) {
                if (parts.length === 1 && ['chronos', 'trace'].includes(parts[0])) {
                    normalizedText = `${parts[0]} *`;
                } else if (!normalizedText.endsWith('*')) {
                    normalizedText = `${normalizedText} *`;
                }
            }
            normalizedText += ' fwd *';
        }

        const afterCommand = normalizedText.replace(/^(chronos|trace)\s*/i, '');
        const meetingElement = /(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|RBA|FED|BOC|SNB|ECB|BOE|BOJ|NZD|MEET)(\d{2})?(\s*[A-Za-z]*)?/i;
        const structElement = /(?:\*|[zhum]\d+[a-z]?(?:(?:\s+|\s*)\d+(?:\.\d+)?[ymdw])+|[zhum]\d+[a-z]?(?:\s*\d+(?:\.\d+)?[ymdw])?|(?:[zhum]\d+[a-z]?|\d+(?:\.\d+)?[ymdw])(?:\s*[,\-]\s*(?:[zhum]\d+[a-z]?|\d+(?:\.\d+)?[ymdw]))*|\d+(?:\.\d+)?[ymdw](?:(?:\s*,?\s*|\s+)\d+(?:\.\d+)?[ymdw])*|\d+(?:\.\d+)?[ymdw]\-\d+(?:\.\d+)?[ymdw])/i;
        const combinedElement = `(?:${meetingElement.source}|${structElement.source})`;
        const structurePattern = new RegExp(`^(?:${combinedElement}(?:\\s+fwd\\s+${combinedElement})?)$`, 'i');
        let beforeStructure = '';
        let structure = '';

        if (structurePattern.test(afterCommand)) {
            beforeStructure = '';
            structure = afterCommand;
        } else {
            const words = afterCommand.split(/\s+/);

            for (let i = 0; i < words.length; i++) {
                const potentialStructure = words.slice(i).join(' ');
                if (structurePattern.test(potentialStructure)) {
                    beforeStructure = words.slice(0, i).join(' ');
                    structure = potentialStructure;
                    break;
                }
            }
        }

        let name = beforeStructure.trim()?.toLowerCase();

        if (['0d', '0m', '0y', 'spot'].includes(name)) name = '';
        if (isIrsPage && irsId && !name && structure) {
            openInSamePage(`${process.env.NEXT_PUBLIC_UI_URL}chronos/${irsId}?str=${structure}`);
            return true;
        }

        if (!name) return false;

        socketEmit('/plots/search', {
            command: 'CHRONOS', phrase: name, structure, tenors: []
        });
        return true;
    }
    return false;
}

export function createDefaultIrsCommandHandlers({
    irs,
    irsId: irsIdFallback,
    router,
    authToken = ''
}) {

    const basis = async (data) => {
        if (!authToken) return;
        const details = { ...data, depId: data?.depId, indepId: data?.indepId, l1: data?.l1 || ['1y'], l2: data?.l2 || ['2y'] }
        const term = details?.l1 ? details.l1.map((tenor) => getTenor(tenor))?.filter((f) => f).slice(0, 4)?.join(',') : getTenor(getValues('structure1'));
        const irsSecurityId = await getIrsSecurity(authToken, { irsId: details?.depId, term: term || '1y' });

        const url = `${process.env.NEXT_PUBLIC_UI_URL}irs/${irsSecurityId}?command=BASIS&l1=${details.l1}&depId=${details.depId}${details.depImm ? '&depImm=1' : ''}${details.depF ? '&depF=1' : ''}&l2=${details.l2}&indepId=${details.indepId}${details.indepImm ? '&indepImm=1' : ''}${details.indepF ? '&indepF=1' : ''}`;
        window.open(url, '_self');
    }
    const id = irs?.id ?? irsIdFallback;
    const base = process.env.NEXT_PUBLIC_UI_URL;

    if (!id) return {
        basis,
        trades: () => openInSamePage(`${base}trades`)
    };

    const meet = ({ irsId }) => {
        if (!irsId) return;
        const { legs } = router.query || {};
        let url = `${base}meet?id=${irsId}`;
        if (legs) {
            url += `&legs=${legs}`;
        }
        openInSamePage(url);
    };

    return {
        plot: ({
            legs, spot, isFwd, imm, secondImm, plotType, thirdImm, command
        } = {}) => {
            if (spot) return;
            const url = `${base}plot/irs/${id}?legs=${legs}${isFwd ? '&command=fwcm' : ''}${imm ? `&immFwd=${imm.label}` : ''}${secondImm ? `&imm=${secondImm.y}y&immLabel=${secondImm.label}` : ''}${thirdImm ? `&immLabel3=${thirdImm.label}` : ''}${plotType ? `&plotType=${plotType}` : ''}${command === 'PLOTA' ? '&advanced=true' : ''}`;
            openInSamePage(url);
        },
        plotF: (legs, spot) => {
            if (spot) return;
            openInSamePage(`${base}plotf/irs/${id}?legs=${legs}`);
        },
        fwcm: ({ irsId: oid, bobCurve, basis6s3sCurve } = {}) => {
            let qs = '';
            if (bobCurve) qs += `?bobCurve=${bobCurve}`;
            if (basis6s3sCurve) qs += `${bobCurve ? '&' : '?'}basis6s3sCurve=${basis6s3sCurve}`;
            openInSamePage(`${base}fwcm/irs/${oid || id}${qs}`);
        },
        edge: () => openInSamePage(`${base}edge/irs/${id}`),
        stats: () => openInSamePage(`${base}stats/irs/${id}`),
        curveCmd: () => openInSamePage(`${base}curve/${id}`),
        arc: (data) => {
            let qs = '';
            if (data?.bobCurve) qs += `?bobCurve=${data.bobCurve}`;
            if (data?.basis6s3sCurve) qs += `${data?.bobCurve ? '&' : '?'}basis6s3sCurve=${data.basis6s3sCurve}`;
            openInSamePage(`${base}arc/irs/${data?.irsId || id}${qs}`);
        },
        chronos: ({ legs }) => openInSamePage(`${base}chronos/${id}?${legs ? `str=${legs}` : 'str=1Y'}`),
        anchorCmd: () => openInSamePage(`${base}anchor?depId=${id}`),
        otc: () => openInSamePage(`${base}otc/${irs?.currency || ''}`),
        trades: () => openInSamePage(`${base}trades`),
        meet,
        basis
    };
}


export function useIrsGlobalSearchEnterCommand({
    irs,
    irsId: irsIdProp = '',
    isIrsPage,
    isBondPage,
    curve,
    validImm,
    forDetailsPage,
    isOTCPage,
    legs,
    immFwd,
    isFwdPlot,
    hasSpreadDiffStartDate,
    irsTerm,
    ovdvCurrency = null,
    valid6s3sBobCcySet,
    setSearchPhrase,
    resetCursor,
    isCorpChildComponent,
    setTicker,
    getHighlightedItemData,
    commandOverrides = {},
}) {
    const noop = useMemo(() => () => { }, []);
    const router = useRouter();
    const auth = useMemo(() => getAuth(), []);

    const defaults = useMemo(
        () => createDefaultIrsCommandHandlers({
            irs,
            irsId: irsIdProp,
            forDetailsPage,
            router,
            authToken: auth?.token
        }),
        [irs, irsIdProp, forDetailsPage, router, auth?.token]
    );

    const irsId = irs?.id ?? irsIdProp;

    const effectiveHandlers = useMemo(() => {
        return {
            ...(defaults || {}),
            ...(commandOverrides || {})
        };
    }, [
        defaults,
        commandOverrides
    ]);

    const {
        plot = noop,
        plotF = noop,
        fwcm = noop,
        edge = noop,
        stats = noop,
        curveCmd = noop,
        arc = noop,
        chronos = noop,
        anchorCmd = noop,
        basis = noop,
        gip = noop,
        otc = noop,
        meet = noop,
        trades = noop
    } = effectiveHandlers;

    const runCommand = useCallback((command, payload) => {
        const key = command?.toUpperCase();
        switch (key) {
            case 'PLOT':
                return plot(payload);
            case 'PLOTF':
                return plotF(payload);
            case 'FWCM':
                return fwcm(payload);
            case 'EDGE':
                return edge(payload);
            case 'STATS':
                return stats(payload);
            case 'CURVE':
            case 'CURVECMD':
                return curveCmd(payload);
            case 'ARC':
                return arc(payload);
            case 'TRACE':
                return chronos(payload);
            case 'ANCHOR':
            case 'ANCHORCMD':
                return anchorCmd(payload);
            case 'BASIS':
                return basis(payload);
            case 'GIP':
                return gip(payload);
            case 'OTC':
                return otc(payload);
            case 'MEET':
                return meet(payload);
            case 'TRADES':
                return trades(payload);
            default:
                return undefined;
        }
    }, [plot, plotF, fwcm, edge, stats, curveCmd, arc, chronos, anchorCmd, basis, gip, otc, meet, trades]);

    const executeIrsEnterCommand = useCallback((searchPhrase) => {
        if (searchPhrase.trim().toLowerCase() === 'watch' || searchPhrase.trim().toLowerCase().startsWith('monitor') || searchPhrase.trim().toLowerCase().startsWith('watchlist')) {
            const url = `${process.env.NEXT_PUBLIC_UI_URL}watchlist`;
            if (forDetailsPage) openInSamePage(url);
            else openTargetPage(url);
            setSearchPhrase('');
            resetCursor();
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('atlas') || searchPhrase.trim().toLowerCase().startsWith('sdr')) {
            const search = searchPhrase.trim().toLowerCase();
            const keywords = search.replace(/^(atlas|sdr)\s*/, '');
            let tab = '';
            if (/(^fx|^fxo|^fx-|fx option|fx-options)/.test(keywords)) {
                tab = 'fx-options';
            } else if (/(^rate|^rates|interest|irs)/.test(keywords)) {
                tab = 'irs';
            }
            const baseUrl = `${process.env.NEXT_PUBLIC_UI_URL}atlas`;
            const url = tab ? `${baseUrl}?tab=${tab}` : baseUrl;
            openInSamePage(url);
            return true;
        }
        if (searchPhrase.toLowerCase().includes('corp')) {
            const words = searchPhrase.trim().toLowerCase().split(/\s+/);
            const ticker = words.find((word) => word !== 'corp');
            if (!ticker) return true;
            if (isCorpChildComponent) {
                setTicker(ticker);
            } else {
                if (ticker) {
                    const url = `${process.env.NEXT_PUBLIC_UI_URL}corp/${ticker}`;
                    openInSamePage(url);
                    return true;
                }
                toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
                return true;
            }
        }

        const itemData = getHighlightedItemData?.();

        if (!itemData && (searchPhrase.toLowerCase().trim() === 'otc'
            || searchPhrase.toLowerCase().startsWith('otc ') || searchPhrase.toLowerCase().endsWith(' otc'))) {
            const index = searchPhrase.toLowerCase().trim().startsWith('otc') ? 1 : 0;
            const currency = searchPhrase.trim().split(' ')[index]?.toLowerCase();
            if (searchPhrase.toLowerCase().trim() === 'otc' && otc) {
                otc();
                return true;
            }
            if (currency) {
                const url = `${process.env.NEXT_PUBLIC_UI_URL}otc/${currency}`;
                openInSamePage(url);
                return true;
            }
            toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
            return true;
        }

        if (isIrsPage && searchPhrase.toLowerCase().includes('gip') && searchPhrase.toLowerCase().startsWith('gip ')) {
            gip(parseGip(searchPhrase));
            setSearchPhrase('');
            return true;
        }

        if (!isBondPage && searchPhrase.toLowerCase().startsWith('bxt')) {
            if (auth.role !== 'Broker') {
                toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
                return true;
            }
            const {
                curveName, tenors, start, dv01s, sizes, trader, isFwd, isValid,
                hasSpreadDiffStartDate: parsedHasSpreadDiffStartDate,
                spreadDiffStartData: parsedSpreadDiffStartData
            } = parseBxtSxtInput(searchPhrase?.trim(), validImm);
            if (tenors?.length > 3 || !isValid) return false;
            const phrase = curveName || (isIrsPage && curve);
            const merged = [...irsTerms, ...validImm];

            let filtered = merged.filter((term) => legs?.includes(term));
            const effectiveHasSpreadDiffStartDate = (hasSpreadDiffStartDate && searchPhrase.trim().toLowerCase() === 'bxt') || parsedHasSpreadDiffStartDate;
            let startLabel = start || immFwd || 'Spot';
            if (isFwdPlot && !immFwd) {
                startLabel = legs[0];
                filtered = legs.slice(1);
            }
            const legsObj = {};
            legs.forEach((value, index) => {
                legsObj[`l${index + 1}`] = value;
            });
            const spreadDiffStartData = effectiveHasSpreadDiffStartDate
                ? (parsedSpreadDiffStartData || buildSpreadDiffStartDateLegs({ legs: legsObj, imm: validImm }))
                : {};
            const cleanTenors = tenors?.length ? tenors : (filtered?.length ? filtered : [irsTerm || '1 Y']);
            socketEmit('/plots/search', {
                phrase,
                tenors: effectiveHasSpreadDiffStartDate ? [addSpaceBeforeUnit(spreadDiffStartData?.endLabel), addSpaceBeforeUnit(spreadDiffStartData?.end1Label)] : cleanTenors,
                start: startLabel,
                command: 'BXT',
                dv01s,
                sizes,
                trader,
                isFwd,
                hasSpreadDiffStartDate: effectiveHasSpreadDiffStartDate,
                ...(effectiveHasSpreadDiffStartDate && {
                    spreadDiffStartData
                })
            });
            return true;
        }
        if (!isBondPage && searchPhrase.toLowerCase().startsWith('sxt')) {
            if (auth.role !== 'Broker') {
                toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' });
                return true;
            }
            const {
                curveName, tenors, start, dv01s, sizes, trader, isFwd, isValid,
                hasSpreadDiffStartDate: parsedHasSpreadDiffStartDate,
                spreadDiffStartData: parsedSpreadDiffStartData
            } = parseBxtSxtInput(searchPhrase?.trim(), validImm);
            if (tenors?.length > 3 || !isValid) return false;
            const phrase = curveName || (isIrsPage && curve);
            const merged = [...irsTerms, ...validImm];

            let filtered = merged.filter((term) => legs?.includes(term));
            const effectiveHasSpreadDiffStartDate = (hasSpreadDiffStartDate && searchPhrase.trim().toLowerCase() === 'sxt') || parsedHasSpreadDiffStartDate;
            let startLabel = start || immFwd || 'Spot';
            if (isFwdPlot && !immFwd) {
                startLabel = legs[0];
                filtered = legs.slice(1);
            }
            const legsObj = {};
            legs.forEach((value, index) => {
                legsObj[`l${index + 1}`] = value;
            });
            const spreadDiffStartData = effectiveHasSpreadDiffStartDate
                ? (parsedSpreadDiffStartData || buildSpreadDiffStartDateLegs({ legs: legsObj, imm: validImm }))
                : {};
            const cleanTenors = tenors?.length ? tenors : (filtered?.length ? filtered : [irsTerm || '1 Y']);
            socketEmit('/plots/search', {
                phrase,
                tenors: effectiveHasSpreadDiffStartDate ? [addSpaceBeforeUnit(spreadDiffStartData?.endLabel), addSpaceBeforeUnit(spreadDiffStartData?.end1Label)] : cleanTenors,
                start: startLabel,
                command: 'SXT',
                dv01s,
                sizes,
                trader,
                isFwd,
                hasSpreadDiffStartDate: effectiveHasSpreadDiffStartDate,
                ...(effectiveHasSpreadDiffStartDate && {
                    spreadDiffStartData
                })
            });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('basis')) {
            const toLower = searchPhrase.trim().toLowerCase().toLowerCase();
            if (toLower === 'basis' && irsId) {
                setSearchPhrase('');
                basis({ depId: irsId, indepId: irsId });
                return true;
            }
            const [structure1, structure2] = toLower.replace('basis', '').split('vs');
            const res1 = parsePlot(`plot ${structure1}`, validImm, true);
            const res2 = parsePlot(`plot ${structure2}`, validImm, true);
            if (!res1.isValid || !res2.isValid) {
                setSearchPhrase('');
                return true;
            }
            const {
                name, tenors, spot, isFwd, imm
            } = res1;
            if (!name && !res2.name && irsId) {
                const params = {
                    l1: tenors,
                    depId: irsId,
                    l2: res2.tenors,
                    indepId: irsId
                };
                if (imm) params.depImm = '1';
                if (isFwd || imm) params.depF = '1';
                if (res2.imm) params.indepImm = '1';
                if (res2.isFwd || res2.imm) params.indepF = '1';
                setSearchPhrase('');
                basis(params);
                return true;
            }
            socketEmit('/plots/search', {
                command: 'BASIS', phrase: name, tenors, spot, isFwd, imm, checkIfMaybeCds: false, uid: 'reg1'
            });
            socketEmit('/plots/search', {
                command: 'BASIS', phrase: res2.name || name, tenors: res2.tenors, spot: res2.spot, isFwd: res2.isFwd, imm: res2.imm, checkIfMaybeCds: false, uid: 'reg2'
            });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('regress')) {
                const toLower = searchPhrase.trim().toLowerCase().toLowerCase();
                if (toLower === 'regress' && irsId) {
                    const url = `${process.env.NEXT_PUBLIC_UI_URL}regress?depId=${irsId}&indepId=${irsId}`;
                    openInSamePage(url);
                }
                const [structure1, structure2] = toLower.replace('regress', '').split('vs');

                const res1 = parsePlot(`plot ${structure1}`, validImm, true);
                const res2 = parsePlot(`plot ${structure2}`, validImm, true);
                if (!res1.isValid || !res2.isValid) {
                    setSearchPhrase('');
                    return true;
                }

                const {
                    name, tenors, spot, isFwd, imm
                } = res1;

                if (!name && !res2.name && irsId) {
                    const url = `${process.env.NEXT_PUBLIC_UI_URL}regress?l1=${tenors}&depId=${irsId}${imm ? '&depImm=1' : ''}${(isFwd || imm) ? '&depF=1' : ''}&l2=${res2.tenors}&indepId=${irsId}${res2.imm ? '&indepImm=1' : ''}${(res2.isFwd || res2.imm) ? '&indepF=1' : ''}`;
                    openInSamePage(url);
                }

                socketEmit('/plots/search', {
                    command: 'REGRESS', phrase: name, tenors, spot, isFwd, imm, checkIfMaybeCds: false, uid: 'reg1'
                });
                socketEmit('/plots/search', {
                    command: 'REGRESS', phrase: res2.name || name, tenors: res2.tenors, spot: res2.spot, isFwd: res2.isFwd, imm: res2.imm, checkIfMaybeCds: false, uid: 'reg2'
                });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('thor')) {
            const toLower = searchPhrase.trim().toLowerCase().toLowerCase();
            if (toLower === 'thor' && irsId) {
                const url = `${process.env.NEXT_PUBLIC_UI_URL}thor?depId=${irsId}&indepId=${irsId}`;
                openInSamePage(url);
            }
            const [structure1, structure2] = toLower.replace('thor', '').split('vs');
            const res1 = parsePlot(`plot ${structure1}`, validImm, true);
            const res2 = parsePlot(`plot ${structure2}`, validImm, true);

            if (!res1.isValid || !res2.isValid) {
                setSearchPhrase('');
                return true;
            }

            if ([res1?.plotType, res2?.plotType].some((type) => type?.startsWith('fBfly'))) return true;

            const {
                name, tenors, spot, isFwd, imm
            } = res1;

            if (!name && !res2.name && irsId) {
                const url = `${process.env.NEXT_PUBLIC_UI_URL}thor?l1=${tenors}&depId=${irsId}${imm ? '&depImm=1' : ''}${(isFwd || imm) ? '&depF=1' : ''}&l2=${res2.tenors}&indepId=${irsId}${res2.imm ? '&indepImm=1' : ''}${(res2.isFwd || res2.imm) ? '&indepF=1' : ''}`;
                openInSamePage(url);
            }

            socketEmit('/plots/search', {
                command: 'THOR', phrase: name, tenors, spot, isFwd, imm, checkIfMaybeCds: false, uid: 'reg1'
            });
            socketEmit('/plots/search', {
                command: 'THOR', phrase: res2.name || name, tenors: res2.tenors, spot: res2.spot, isFwd: res2.isFwd, imm: res2.imm, checkIfMaybeCds: false, uid: 'reg2'
            });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('anchor')) {
            const toLower = searchPhrase.trim().toLowerCase();
            if (toLower === 'anchor' && irsId) {
                anchorCmd();
                return true;
            }
            const structure = toLower.replace('anchor', '');
            const res = parsePlot(`plot ${structure}`, validImm, true);

            if (!res.isValid) {
                setSearchPhrase('');
                return true;
            }

            if (res?.plotType?.startsWith('fBfly')) return true;

            const {
                name, tenors, spot, isFwd, imm
            } = res;

            if (!name && irsId) {
                const url = `${process.env.NEXT_PUBLIC_UI_URL}anchor?l1=${tenors}&depId=${irsId}${imm ? '&depImm=1' : ''}${(isFwd || imm) ? '&depF=1' : ''}`;
                openInSamePage(url);
            }

            socketEmit('/plots/search', {
                command: 'ANCHOR', phrase: name, tenors, spot, isFwd, imm, checkIfMaybeCds: false, uid: 'reg1'
            });
            return true;
        }
        if (handleTraceChronosBranch({
            searchPhrase,
            isIrsPage,
            irsId,
            chronos,
            openInSamePage,
            irsTerm
        })) {
            return true;
        }

        if (!searchPhrase.toLowerCase().includes('inp')
            && !searchPhrase.toLowerCase().startsWith('inp ')
            && parsePlot(searchPhrase, validImm, isIrsPage).isValid
        ) {
            const {
                name, tenors, spot, isFwd, imm, checkIfMaybeCds, secondImm, plotType, thirdImm, isPlotF, isPlotA, isBasis6s3s, isBob, isMeet, label
            } = parsePlot(searchPhrase, validImm, isIrsPage);
            if (isMeet) {
                socketEmit('/get/irs/meeting-date/:irsId', { irsId: null, label: label, tag: 'search' });
                return true;
            }
            if (!isFwd && tenors.length > 3) { toast('Please enter a valid command', { type: 'error', toastId: 'invalid-command' }); return true; }
            if (isBasis6s3s || isBob) {
                if (!name && !irs?.currency) return false;
                let currency = name || irs?.currency;
                if (!valid6s3sBobCcySet.has(currency.toUpperCase()) && isBob) currency = 'AUD';
                socketEmit('/basis-plots/search', {
                    isBasis6s3s, isBob, currency: !isBob ? 'AUD' : currency, tenors, spot, isFwd, imm, checkIfMaybeCds, secondImm, plotType, thirdImm
                });
                return true;
            }
            if (!name && (forDetailsPage || isOTCPage)) {
                if (isPlotF) {
                    plotF(tenors, spot, isFwd, imm, secondImm);
                } else {
                    plot({
                        legs: tenors, spot, isFwd, imm, secondImm, plotType, thirdImm, command: isPlotA ? 'PLOTA' : null
                    });
                }
                return true;
            }
            if (/^(plot|roll|plotf|plota)\s+.+/i.test(searchPhrase)) {
                const command = isPlotF ? 'PLOTF' : isPlotA ? 'PLOTA' : null;
                socketEmit('/plots/search', {
                    phrase: name, tenors, spot, isFwd, imm, checkIfMaybeCds, secondImm, plotType, thirdImm, command
                });
                return true;
            }
        }
        if ((searchPhrase.trim().toLowerCase() === 'bob' || searchPhrase.trim().toLowerCase() === '6s3s') && isIrsPage) {
            const isBob = searchPhrase.trim().toLowerCase() === 'bob';
            let currency = irs?.currency || '';
            if (!valid6s3sBobCcySet.has(currency.toUpperCase()) && isBob) currency = 'AUD';
            socketEmit('/basis-plots/search', {
                isBasis6s3s: !isBob, isBob, currency: !isBob ? 'AUD' : currency, isFwcm: true
            });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('fwcm') || searchPhrase.trim().toLowerCase().endsWith('fwcm')) {
            if (searchPhrase.trim().toLowerCase() === 'fwcm' && isIrsPage) fwcm({});
            else {
                const name = searchPhrase.trim().toLowerCase().replace('fwcm', '').trim() || 'inr ndois';
                socketEmit('/plots/search', { phrase: name, command: 'fwcm', tenors: [] });
            }
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('edge') || searchPhrase.trim().toLowerCase().endsWith('edge')) {
            if (searchPhrase.trim().toLowerCase() === 'edge' && isIrsPage) edge();
            else {
                const name = searchPhrase.trim().toLowerCase().replace('edge', '').trim() || 'inr ndois';
                socketEmit('/plots/search', { phrase: name, command: 'edge', tenors: [] });
            }
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('stats') || searchPhrase.trim().toLowerCase().endsWith('stats')) {
            if (searchPhrase.trim().toLowerCase() === 'stats' && isIrsPage) stats();
            else {
                const name = searchPhrase.trim().toLowerCase().replace('stats', '').trim() || 'inr ndois';
                socketEmit('/plots/search', { phrase: name, command: 'stats', tenors: [] });
            }
            return true;
        }

        if (searchPhrase.trim().toLowerCase().startsWith('curve') || searchPhrase.trim().toLowerCase().endsWith('curve')) {
            if (searchPhrase.trim().toLowerCase() === 'curve' && isIrsPage) curveCmd();
            else {
                const name = searchPhrase.trim().toLowerCase().replace('curve', '').trim() || 'inr ndois';
                socketEmit('/plots/search', { phrase: name, command: 'CURVE', tenors: [] });
            }
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('arc') || searchPhrase.trim().toLowerCase().endsWith('arc')) {
            if (searchPhrase.trim().toLowerCase() === 'arc' && isIrsPage) arc();
            else {
                const name = searchPhrase.trim().toLowerCase().replace('arc', '').trim() || 'inr ndois';
                socketEmit('/plots/search', { phrase: name, command: 'arc', tenors: [] });
            }
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('meet')) {
            if (searchPhrase.trim().toLowerCase() === 'meet' && isIrsPage) {
                meet({ irsId });
            } else {
                const name = searchPhrase.trim().toLowerCase().replace('meet', '').trim() || 'aud ois';
                socketEmit('/plots/search', { phrase: name, command: 'meet', tenors: [] });
            }
            return true;
        }

        if (searchPhrase.trim().toLowerCase().startsWith('ovdv') || searchPhrase.trim().toLowerCase().endsWith('ovdv')) {
            const name = searchPhrase.trim().toLowerCase().replace('ovdv', '').trim() || ovdvCurrency || 'USDINR';
            socketEmit('/get/fx-options/currency-pair', { currency: name, command: 'ovdv' });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('omon')) {
            const name = searchPhrase.trim().toLowerCase().replace('omon', '').trim() || ovdvCurrency || 'USDINR';
            socketEmit('/get/fx-options/currency-pair', { currency: name, command: 'omon' });
            return true;
        }
        if (searchPhrase.trim().toLowerCase().startsWith('dslice')) {
            const name = searchPhrase.trim().toLowerCase().replace('dslice', '').trim() || ovdvCurrency || 'USDINR';
            socketEmit('/get/fx-options/currency-pair', { currency: name, command: 'dslice' });
            return true;
        }

        return false;
    }, [
        getHighlightedItemData,
        isIrsPage,
        isBondPage,
        curve,
        validImm,
        auth,
        forDetailsPage,
        isOTCPage,
        legs,
        immFwd,
        isFwdPlot,
        hasSpreadDiffStartDate,
        irsTerm,
        ovdvCurrency,
        irs,
        irsId,
        valid6s3sBobCcySet,
        setSearchPhrase,
        resetCursor,
        isCorpChildComponent,
        setTicker,
        plot,
        plotF,
        fwcm,
        edge,
        stats,
        curveCmd,
        arc,
        chronos,
        anchorCmd,
        basis,
        gip,
        otc,
        meet
    ]);

    return { executeIrsEnterCommand, effectiveHandlers, runCommand };
}
